import React, { useRef, useState } from 'react';
import AddIcon from '@mui/icons-material/Add';
import { designSystem } from '@/styles/theme';
import { FilterPanel, FilterPanelControls, useIsMobileWidth } from '@/components/common/filter';
import PickerCalendar from '@/components/common/datePicker/PickerCalendar';
import TimeWheel from '@/components/common/datePicker/TimeWheel';
import { TimeGrid, TimeHint } from '@/components/common/datePicker/TimeSlots';
import {
  DateAddChip,
  DateChip,
  DateChipRemove,
  PcGrid,
  PcSelectButton,
  PcTimeColumn,
  PickerBody,
  PickerNote,
  PickerTrigger,
  SectionLabel,
  StepHint,
  StepPane,
  StepTabs,
  SummaryLine,
} from '@/components/common/datePicker/pickerStyles';
import {
  AmPm,
  ampmOf,
  formatDateOnly,
  formatDayKo,
  formatDayShort,
  formatRangeKo,
  formatTimeKo,
  initialMonth,
  isDateTimeAllowed,
  isDayAllowedForDateTime,
  isSlotTime,
  joinDateTime,
  monthStart,
  plusTwoHours,
  splitDateTime,
} from '@/components/common/datePicker/pickerUtils';

/**
 * 공용 날짜·시간 선택기(docs/seminar-date-picker-plan.md — 세미나 폼에서 시작, 2026-09-29 사용자 결정으로 앞으로 달력은 이것을 쓴다).
 * 예전 react-datepicker 기반 CustomDatePicker(채용 폼)와는 따로 간다.
 *
 * 칸을 누르면 폰(≤768px)은 아래 시트 — 일시는 한 번에 하나씩(① 날짜 → ② 시간, 2차 개편 계약 6장), 기간·하루는 달력만.
 * PC 는 칸 아래 팝업(왼쪽 달력 · 오른쪽 시간 칸 2열, 누르면 바로 반영)으로 연다. 다른 시각은 휠(TimeWheel)로. 창은 공용 필터 창(FilterPanel)을 그대로 쓴다 —
 * 열릴 때만 그리고, 시트는 바깥 누르기·아래로 끌기·뒤로가기로 닫히며(반영 안 함) 방문 기록·스크롤 잠금 규칙도 같다.
 *
 * 값 모양은 기존 그대로 — datetime: `YYYY-MM-DDTHH:mm`, date: `YYYY-MM-DD`, range: `YYYY-MM-DD` 둘.
 */

/** PC 팝업 폭 — 달력 + 시간 칸 */
const POPOVER_WIDTH_DATETIME_PX: number = 560;
const POPOVER_WIDTH_DATE_PX: number = 360;
/** 시각만 고르는 칸(timeOnly) 팝업 폭 — 시간 칸만 */
const POPOVER_WIDTH_TIME_PX: number = 280;
/** MUI 창(modal 1300) 안에서 쓸 때 PC 팝업 층 — 모달 위에 뜨는 달력은 popover 토큰(UI 가이드 5장) */
const IN_MODAL_POPOVER_Z: number = designSystem.zIndex.popover;

interface CommonProps {
  /** 시트·팝업 제목이자 칸 이름(예: "시작 일시") */
  title: string;
  /** 시트 제목 오른쪽 작은 안내(기간·하루 — 일시는 단계 "1/2") */
  subtitle?: string;
  placeholder: string;
  hasError?: boolean;
  icon?: 'calendar' | 'clock';
  /** MUI 창(Modal) 안에서 쓸 때 — PC 팝업을 창보다 위에 띄운다(폰 시트는 원래 창보다 위) */
  inModal?: boolean;
  /** 달력 아래 작은 안내(하루 고르는 법 등)를 보일지 — 기본 보임 */
  showNotes?: boolean;
}

export interface DateTimeModeProps extends CommonProps {
  mode: 'datetime';
  value: string;
  onChange: (value: string) => void;
  /** 이보다 앞 일시는 못 고른다(지금·시작 1분 뒤 등) */
  min?: Date | null;
  /** 이보다 뒤 일시는 못 고른다(예: 모집 마감 ≤ 세미나 시작) */
  max?: Date | null;
  /** 종료 칸 지름길 — 값이 없을 때 이 시작 일시의 날짜와 2시간 뒤를 골라 ② 시간에서 연다(당일 종료만) */
  shortcutStart?: string | null;
  /**
   * 날짜가 정해진 칸의 시각만 고를 때(채용 날짜별·요일별 시간) — 칸에는 "09:30"만 보이고, 날짜 단계·달력 없이 시간 칸만 연다
   * (날짜는 제목에 둔다). 날짜는 [min] 의 날 — [min]·[max] 를 그날 00:00·23:59 로 넘긴다.
   */
  timeOnly?: boolean;
  /** 시각이 아직 없을 때 ② 시간이 처음 보여 줄 오전/오후 탭 — 기본 오후(지금까지 동작). 근무 시작처럼 오전이 흔한 칸만 'AM' */
  defaultAmpm?: AmPm;
}

export interface DateModeProps extends CommonProps {
  mode: 'date';
  value: string;
  onChange: (value: string) => void;
  /** `YYYY-MM-DD` — 이보다 앞 날은 못 고른다 */
  minDate?: string;
  /** 한 해씩 넘기는 버튼(« ») — 몇 년 전 날짜를 고르는 칸(이력서 경력 기간)만. 켜면 칸 글자도 연도까지 "2024.10.07" */
  yearNav?: boolean;  /** `YYYY-MM-DD` — 이보다 뒤 날은 못 고른다(예: 경력 시작일 ≤ 오늘) */
  maxDate?: string;
}

export interface RangeModeProps extends CommonProps {
  mode: 'range';
  start: string;
  end: string;
  onChange: (start: string, end: string) => void;
  /** `YYYY-MM-DD` — 이보다 앞 날은 시작일로 못 고른다 */
  minDate?: string;
  /** `YYYY-MM-DD` — 이보다 뒤 날은 못 고른다(예: 쿠폰 사용 기간 ≤ 모집 마감) */
  maxDate?: string;
}

export interface DatesModeProps extends CommonProps {
  mode: 'dates';
  value: string[];
  onChange: (value: string[]) => void;
  minDate?: string;
}
export type DateTimePickerProps = DateTimeModeProps | DateModeProps | RangeModeProps | DatesModeProps;

// ───────── 일시(datetime) ─────────

type DateTimeStep = 'date' | 'time';

const DateTimeField: React.FC<DateTimeModeProps> = ({
  title, placeholder, hasError = false, icon = 'calendar', value, onChange, min, max, shortcutStart, inModal = false, timeOnly = false,
  defaultAmpm = 'PM',
}) => {
  const mobile: boolean = useIsMobileWidth();
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState<boolean>(false);
  const [step, setStep] = useState<DateTimeStep>('date');
  const [direction, setDirection] = useState<'forward' | 'back'>('forward');
  const [date, setDate] = useState<string | null>(null);
  const [time, setTime] = useState<string | null>(null);
  const [ampm, setAmpm] = useState<AmPm>('PM');
  const [wheel, setWheel] = useState<boolean>(false);
  const [month, setMonth] = useState<Date>(() => monthStart(new Date()));

  const dayDisabled = (day: string): boolean => !isDayAllowedForDateTime(day, min, max);
  const timeAllowed = (day: string | null, at: string): boolean => !day || isDateTimeAllowed(day, at, min, max);

  /**
   * 열기 — 값이 있으면 ② 시간에서(날짜 골라진 채), 종료 칸 지름길이면 시작 날짜 + 시작 2시간 뒤를 골라 ② 시간에서,
   * 둘 다 아니면 ① 날짜에서. 버튼에 없는 시각(예 14:10)이면 휠 상태로.
   */
  const openPicker = (): void => {
    const saved = splitDateTime(value);
    const start = splitDateTime(shortcutStart);
    // 저장된 날짜가 지금은 못 고르는 날이면(시작 일시를 뒤로 옮겨 종료가 시작보다 앞이 된 경우 등) 없는 셈 치고 새로 고르게 한다.
    // 그대로 ② 시간을 열면 고를 수 있는 시각이 하나도 없어 휠이 오전·오후를 계속 오간다.
    const savedUsable: boolean = !!saved.date && !dayDisabled(saved.date);
    let initialDate: string | null = savedUsable ? saved.date : null;
    let initialTime: string | null = savedUsable ? saved.time : null;
    if (!initialDate && timeOnly && min && !dayDisabled(formatDateOnly(min))) initialDate = formatDateOnly(min);
    if (!initialTime && timeOnly && start.date === initialDate && start.time) {
      const plus: string | null = plusTwoHours(start.time);
      initialTime = plus && initialDate && isDateTimeAllowed(initialDate, plus, min, max) ? plus : null;
    }
    if (!initialDate && start.date && !dayDisabled(start.date)) {
      initialDate = start.date;
      const plus: string | null = plusTwoHours(start.time);
      initialTime = plus && isDateTimeAllowed(start.date, plus, min, max) ? plus : null;
    }
    setDate(initialDate);
    setTime(initialTime);
    setAmpm(initialTime ? ampmOf(initialTime) : defaultAmpm);
    setWheel(!!initialTime && !isSlotTime(initialTime));
    setStep(initialDate ? 'time' : 'date');
    setDirection('forward');
    setMonth(initialMonth(initialDate, min));
    setOpen(true);
  };

  const complete: boolean = !!date && !!time && timeAllowed(date, time);
  const commit = (day: string, at: string): void => onChange(joinDateTime(day, at));

  const pickDate = (day: string): void => {
    setDate(day);
    // 그날엔 못 고르는 시각이면 다시 고르게 비운다(휠은 가까운 값으로 옮겨 간다)
    const keep: boolean = !!time && timeAllowed(day, time);
    if (!keep && !wheel) setTime(null);
    if (mobile) {
      setDirection('forward');
      setStep('time');
    } else if (keep && time) {
      commit(day, time);
    }
  };

  const pickTime = (at: string, controls: FilterPanelControls): void => {
    setTime(at);
    if (!mobile && date) {
      commit(date, at);
      controls.close();
    }
  };

  const toggleWheel = (): void => {
    // 휠에서 버튼으로 돌아갈 때 버튼에 없는 시각은 비운다
    if (wheel && time && !isSlotTime(time)) setTime(null);
    if (wheel && time && isSlotTime(time)) setAmpm(ampmOf(time));
    setWheel(!wheel);
  };

  const saved = splitDateTime(value);
  const triggerText: string = saved.date && saved.time ? (timeOnly ? saved.time : `${formatDayShort(saved.date)} ${formatTimeKo(saved.time)}`) : '';
  const isDisabledAt = (at: string): boolean => !timeAllowed(date, at);

  const grid = (controls: FilterPanelControls, compact: boolean) => (
    <TimeGrid
      ampm={ampm}
      onAmpmChange={setAmpm}
      selected={time}
      isDisabled={isDisabledAt}
      onPick={(at: string) => pickTime(at, controls)}
      label={title}
      compact={compact}
      inactive={!date}
    />
  );

  const wheelView = (
    <TimeWheel value={time ?? ''} onChange={(at: string) => setTime(at || null)} isDisabled={isDisabledAt} label={title} />
  );

  const calendar = (
    <PickerCalendar
      month={month}
      onMonthChange={setMonth}
      isPicked={(day: string) => day === date}
      isDisabled={dayDisabled}
      onPick={pickDate}
    />
  );

  const mobileApplyLabel: string | undefined = step === 'time' ? (complete && time ? `${formatTimeKo(time)} 선택` : '시간을 골라 주세요') : undefined;

  return (
    <>
      <PickerTrigger
        ref={triggerRef}
        icon={icon}
        text={triggerText}
        placeholder={placeholder}
        label={title}
        hasError={hasError}
        open={open}
        onClick={() => (open ? setOpen(false) : openPicker())}
      />
      <FilterPanel
        open={open}
        anchorEl={triggerRef.current}
        title={title}
        onClose={() => setOpen(false)}
        onApply={() => {
          if (date && time && complete) commit(date, time);
          setOpen(false);
        }}
        applyLabel={mobile ? mobileApplyLabel : undefined}
        applyDisabled={!complete}
        popoverWidth={timeOnly ? POPOVER_WIDTH_TIME_PX : POPOVER_WIDTH_DATETIME_PX}
        popoverZIndex={inModal ? IN_MODAL_POPOVER_Z : undefined}
      >
        {(controls: FilterPanelControls) => (timeOnly ? (
          // 시각만 — 날짜 단계·달력 없이 시간 칸(30분 버튼 / 다른 시간 직접 입력 휠)
          <PickerBody data-no-swipe-back>
            {mobile ? (
              <>
                {wheel ? wheelView : grid(controls, false)}
                <TimeHint wheel={wheel} onToggle={toggleWheel} />
              </>
            ) : (
              <PcTimeColumn>
                {wheel ? (
                  <>
                    {wheelView}
                    <PcSelectButton
                      type="button"
                      disabled={!complete}
                      onClick={() => {
                        if (date && time && complete) commit(date, time);
                        controls.close();
                      }}
                    >
                      {complete && time ? `${formatTimeKo(time)} 선택` : '선택'}
                    </PcSelectButton>
                  </>
                ) : (
                  <div className="pc-time-scroll">{grid(controls, true)}</div>
                )}
                <TimeHint wheel={wheel} onToggle={toggleWheel} compact />
              </PcTimeColumn>
            )}
          </PickerBody>
        ) : mobile ? (
          <PickerBody data-no-swipe-back>
            <StepTabs
              label={`${title} 단계`}
              current={step}
              tabs={[
                { key: 'date', label: '날짜', value: date ? formatDayKo(date) : null },
                { key: 'time', label: '시간', value: time && complete ? formatTimeKo(time) : null, disabled: !date },
              ]}
              onSelect={(key: string) => {
                if (key === step) return;
                setDirection(key === 'time' ? 'forward' : 'back');
                setStep(key as DateTimeStep);
              }}
            />
            {step === 'date' ? (
              <StepPane key="date" direction={direction}>
                {calendar}
                <StepHint>날짜를 누르면 시간으로 넘어가요</StepHint>
              </StepPane>
            ) : (
              <StepPane key="time" direction={direction}>
                {wheel ? wheelView : grid(controls, false)}
                <TimeHint wheel={wheel} onToggle={toggleWheel} />
              </StepPane>
            )}
          </PickerBody>
        ) : (
          <PickerBody data-no-swipe-back>
            <PcGrid>
              {calendar}
              <PcTimeColumn>
                <SectionLabel>
                  시간
                  {!date && <span>날짜를 먼저</span>}
                </SectionLabel>
                {wheel ? (
                  <>
                    {wheelView}
                    <PcSelectButton
                      type="button"
                      disabled={!complete}
                      onClick={() => {
                        if (date && time && complete) commit(date, time);
                        controls.close();
                      }}
                    >
                      {complete && time ? `${formatTimeKo(time)} 선택` : '선택'}
                    </PcSelectButton>
                  </>
                ) : (
                  <div className="pc-time-scroll">{grid(controls, true)}</div>
                )}
                <TimeHint wheel={wheel} onToggle={toggleWheel} compact />
              </PcTimeColumn>
            </PcGrid>
          </PickerBody>
        ))}
      </FilterPanel>
    </>
  );
};

// ───────── 날짜 하루(date) ─────────

const DateField: React.FC<DateModeProps> = ({ title, subtitle, placeholder, hasError = false, icon = 'calendar', value, onChange, minDate, maxDate, inModal = false, yearNav = false }) => {
  const mobile: boolean = useIsMobileWidth();
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState<boolean>(false);
  const [date, setDate] = useState<string | null>(null);
  const [month, setMonth] = useState<Date>(() => monthStart(new Date()));

  const openPicker = (): void => {
    const saved = splitDateTime(value).date;
    const usable = saved && (!minDate || saved >= minDate) && (!maxDate || saved <= maxDate) ? saved : null;
    setDate(usable);
    setMonth(initialMonth(usable, minDate));
    setOpen(true);
  };

  return (
    <>
      <PickerTrigger
        ref={triggerRef}
        icon={icon}
        text={value ? (yearNav ? value.slice(0, 10).replace(/-/g, '.') : formatDayShort(value)) : ''}
        placeholder={placeholder}
        label={title}
        hasError={hasError}
        open={open}
        onClick={() => (open ? setOpen(false) : openPicker())}
      />
      <FilterPanel
        open={open}
        anchorEl={triggerRef.current}
        title={title}
        subtitle={mobile ? subtitle : undefined}
        onClose={() => setOpen(false)}
        onApply={() => {
          if (date) onChange(date);
          setOpen(false);
        }}
        applyLabel={mobile ? '확인' : undefined}
        applyDisabled={!date}
        popoverWidth={POPOVER_WIDTH_DATE_PX}
        popoverZIndex={inModal ? IN_MODAL_POPOVER_Z : undefined}
      >
        {(controls: FilterPanelControls) => (
          <PickerBody data-no-swipe-back>
            {mobile && (
              <SummaryLine aria-live="polite">
                {date ? <b>{formatDayKo(date)}</b> : <em>날짜를 골라 주세요</em>}
              </SummaryLine>
            )}
            <PickerCalendar
              month={month}
              onMonthChange={setMonth}
              isPicked={(day: string) => day === date}
              yearNav={yearNav}
              isDisabled={(day: string) => (!!minDate && day < minDate) || (!!maxDate && day > maxDate)}
              onPick={(day: string) => {
                setDate(day);
                if (!mobile) {
                  onChange(day);
                  controls.close();
                }
              }}
            />
          </PickerBody>
        )}
      </FilterPanel>
    </>
  );
};

// ───────── 기간(range, 날짜만) ─────────

const RangeField: React.FC<RangeModeProps> = ({
  title, subtitle, placeholder, hasError = false, icon = 'calendar', start, end, onChange, minDate, maxDate, inModal = false, showNotes = true,
}) => {
  const mobile: boolean = useIsMobileWidth();
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState<boolean>(false);
  const [draftStart, setDraftStart] = useState<string | null>(null);
  const [draftEnd, setDraftEnd] = useState<string | null>(null);
  const [month, setMonth] = useState<Date>(() => monthStart(new Date()));

  const openPicker = (): void => {
    const savedStart: string | null = splitDateTime(start).date;
    const savedEnd: string | null = splitDateTime(end).date;
    setDraftStart(savedStart);
    setDraftEnd(savedStart ? savedEnd ?? savedStart : null);
    setMonth(initialMonth(savedStart, minDate));
    setOpen(true);
  };

  /** 시작일 → 종료일 차례. 둘 다 있으면 새 시작일부터, 종료일 차례에 시작일보다 앞을 누르면 그날이 새 시작일 */
  const pick = (day: string, controls: FilterPanelControls): void => {
    const choosingEnd: boolean = !!draftStart && !draftEnd && day >= draftStart;
    if (choosingEnd && draftStart) {
      setDraftEnd(day);
      if (!mobile) {
        onChange(draftStart, day);
        controls.close();
      }
      return;
    }
    setDraftStart(day);
    setDraftEnd(null);
    // PC 는 누르면 바로 반영 — 시작일만 고른 상태는 하루 행사(종료 = 시작)
    if (!mobile) onChange(day, day);
  };

  let summary: React.ReactNode;
  if (!draftStart) summary = <em>시작일을 골라 주세요</em>;
  else if (!draftEnd) summary = (<><b>{formatDayKo(draftStart)}</b><span aria-hidden>~</span><em>종료일을 골라 주세요</em></>);
  else summary = <b>{formatRangeKo(draftStart, draftEnd)}</b>;

  const savedStart: string | null = splitDateTime(start).date;
  const savedEnd: string | null = splitDateTime(end).date;
  let triggerText: string = '';
  if (savedStart && savedEnd && savedStart !== savedEnd) triggerText = `${formatDayShort(savedStart)} ~ ${formatDayShort(savedEnd)}`;
  else if (savedStart) triggerText = `${formatDayShort(savedStart)} 하루`;

  const today: string = formatDateOnly(new Date());

  return (
    <>
      <PickerTrigger
        ref={triggerRef}
        icon={icon}
        text={triggerText}
        placeholder={placeholder}
        label={title}
        hasError={hasError}
        open={open}
        onClick={() => (open ? setOpen(false) : openPicker())}
      />
      <FilterPanel
        open={open}
        anchorEl={triggerRef.current}
        title={title}
        subtitle={mobile ? subtitle : undefined}
        onClose={() => setOpen(false)}
        onApply={() => {
          if (draftStart) onChange(draftStart, draftEnd ?? draftStart);
          setOpen(false);
        }}
        applyLabel={mobile ? '확인' : undefined}
        applyDisabled={!draftStart}
        popoverWidth={POPOVER_WIDTH_DATE_PX}
        popoverZIndex={inModal ? IN_MODAL_POPOVER_Z : undefined}
      >
        {(controls: FilterPanelControls) => (
          <PickerBody data-no-swipe-back>
            <SummaryLine aria-live="polite">{summary}</SummaryLine>
            <PickerCalendar
              month={month}
              onMonthChange={setMonth}
              isPicked={(day: string) => day === draftStart || day === draftEnd}
              // 종료일 차례에는 시작일보다 앞 날도 누를 수 있다(새 시작일이 된다) — 하한·상한만 흐리게
              isDisabled={(day: string) => (!!minDate && day < minDate) || (!!maxDate && day > maxDate)}
              onPick={(day: string) => pick(day, controls)}
              rangeStart={draftStart}
              rangeEnd={draftEnd}
            />
            {showNotes && draftStart && !draftEnd && (
              <PickerNote>
                {mobile ? '시작일만 고르고 [확인]을 누르면 하루 일정이에요.' : '종료일을 누르면 기간이 정해져요. 하루면 그대로 닫아도 돼요.'}
              </PickerNote>
            )}
            {showNotes && !draftStart && minDate && minDate > today && <PickerNote>{`${formatDayKo(minDate)}부터 고를 수 있어요.`}</PickerNote>}
          </PickerBody>
        )}
      </FilterPanel>
    </>
  );
};

/** 여러 날을 고른 뒤 [선택]할 때만 반영한다. */
const DatesField: React.FC<DatesModeProps> = ({ title, placeholder, value, onChange, minDate, hasError = false, inModal }) => {
  const mobile = useIsMobileWidth();
  const trigger = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const [days, setDays] = useState<string[]>([]);
  const [month, setMonth] = useState(() => monthStart(new Date()));
  const apply = (): void => { onChange([...days].sort()); setOpen(false); };
  return <>
    <DateAddChip ref={trigger} type="button" aria-label={title} aria-haspopup="dialog" aria-expanded={open} aria-invalid={hasError}
      onClick={() => { setDays(value); setMonth(initialMonth(value[0], minDate)); setOpen(true); }}><AddIcon aria-hidden />{placeholder.replace(/^[＋+]\s*/, '')}</DateAddChip>
    <FilterPanel open={open} anchorEl={trigger.current} title={title} onClose={() => setOpen(false)}
      onApply={apply} applyLabel={mobile ? (days.length ? '선택' : '닫기') : undefined} popoverWidth={POPOVER_WIDTH_DATE_PX}
      popoverZIndex={inModal ? IN_MODAL_POPOVER_Z : undefined}>
      <PickerBody data-no-swipe-back>
        <PickerCalendar month={month} onMonthChange={setMonth} isPicked={day => days.includes(day)}
          isDisabled={day => !!minDate && day < minDate}
          onPick={day => setDays(previous => previous.includes(day) ? previous.filter(d => d !== day) : [...previous, day].sort())} />
        {/* 고른 날 — 테두리 박스 없이 칩만(2026-10-01 사용자 지시). 고른 날이 없으면 자리도 차지하지 않는다 */}
        {days.length > 0 && (
          <div aria-live="polite" style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 10 }}>
            {days.map(day => <DateChip type="button" key={day} onClick={() => setDays(previous => previous.filter(d => d !== day))}
              aria-label={`${formatDayKo(day)} 삭제`}>{formatDayShort(day)}<DateChipRemove aria-hidden /></DateChip>)}
          </div>
        )}
        {/* PC 는 이 버튼 하나만(창 아래 적용 버튼은 폰에서만) — 달력 폭에 맞춰 가득 */}
        {!mobile && <PcSelectButton type="button" style={{ display: 'block', width: '100%' }} onClick={apply}>{days.length ? '선택' : '닫기'}</PcSelectButton>}
      </PickerBody>
    </FilterPanel>
  </>;
};

/** 날짜·시간 선택기 — mode: datetime(일시) / date(하루) / range(기간) */
const DateTimePicker: React.FC<DateTimePickerProps> = (props) => {
  if (props.mode === 'datetime') return <DateTimeField {...props} />;
  if (props.mode === 'date') return <DateField {...props} />;
  if (props.mode === 'dates') return <DatesField {...props} />;
  return <RangeField {...props} />;
};

export default DateTimePicker;
